export interface LiveOutstandingValueResponse {
  outStandingAmt: number;
}

// GET /sales/live/v2/by-channel — one row per channel.
// Shares a response shape with /sales/live/v2/staff-perfomance, but only the fields that make
// sense for a per-channel rollup are populated: `channel` is real and `staff_name` is always
// null (the mirror image of staff-perfomance). Anything the endpoint does not compute comes
// back null rather than being omitted.
export interface LiveByChannelItem {
  orderCount: number;
  netSales: number;
  tip: number;
  tax: number | null;
  discount: number;
  grandTotal: number;
  gratuity: number;
  tipAndGratuity: number | null;
  ordersWithTips: number | null;
  ordersWithGratuity: number | null;
  otherCharges: number | null;
  outStandingAmt: number;
  channel: string;
  timezoneCode: string | null;
  /** Always null on this endpoint — use staff-perfomance for anything staff-attributed. */
  staff_name: string | null;
  refund: number | null;
  todayAvgOrder: number | null;
  guestCount: number | null;
  spendPerGuest: number | null;
}

export interface LiveItemSalesV2Item {
  itemName: string;
  categoryName: string | null;
  quantity: number;
  price: number;
}

export interface SnapshotDetailsResponse {
  orderCount: number;
  netSales: number;
  tip: number;
  tax: number | null;
  discount: number;
  guestCount: number;
  grandTotal: number;
  gratuity: number;
  // ZAR-1762 — the unified Tips & Gratuity KPI reads these three directly instead of
  // deriving them from the staff-performance buckets:
  //   tipAndGratuity     — combined total shown on the card face
  //   ordersWithTips     — count of tip-bearing orders (drill-in, Tips section)
  //   ordersWithGratuity — count of gratuity-bearing orders; the base Avg/Gratuity Order divides by
  tipAndGratuity: number;
  ordersWithTips: number;
  ordersWithGratuity: number;
  otherCharges?: number;
  outStandingAmt?: number;
  spendPerGuest?: number;
  channel: null;
  timezoneCode: null;
}

export interface LivePaymentsItem {
  tenderType: string;
  tenderAmount: number;
  transactionCount: number;
}

export interface LiveRefundSummaryItem {
  cancelReason: string;
  removedQty: number;
  orderCount: number;
  cancelledAmt: number;
}

export interface LiveVoidSummaryItem {
  cancelReason: string;
  removedQty: number;
  orderCount: number;
  cancelledAmt: number;
}

export interface LiveRefundDetailsItem {
  orderNo: string;
  orderDate?: string;
  voidedTime: string;
  staffName: string;
  cancelReason: string;
  cancelledAmt: number;
  itemName: string | null;
  channel: string;
}

export interface LiveVoidDetailsItem {
  orderNo: string;
  orderDate?: string;
  voidedTime: string;
  staffName: string;
  cancelReason: string;
  cancelledAmt: number;
  itemName: string;
}

export interface LiveVoidDetailsResponse {
  content: LiveVoidDetailsItem[];
  totalPages?: number;
  totalElements?: number;
  number?: number;
  size?: number;
}

export interface LiveRefundDetailsResponse {
  content: LiveRefundDetailsItem[];
  totalPages?: number;
  totalElements?: number;
  number?: number;
  size?: number;
}

export interface LiveHourlySalesItem {
  hour: string;
  grandTotal: number;
  itemTotal: number;
  orderCount: number;
}

// Response item for GET /sales/live/v2/hourly-sales?locationId=&startDate=&endDate=
export interface HourlySalesItem {
  hour: string;
  hourValue: number;
  grandTotal: number;
  itemTotal: number;
  orderCount: number;
}

export interface HourlySalesResponse {
  hours: HourlySalesItem[];
  operatingStartHour: number;
  operatingEndHour: number;
  operatingHourCount: number;
  closed: boolean;
}

// Response item for GET /sales/live/v2/staff-perfomance (Today only)
// Drives the Tips leaderboard and the Total Discounts stamp KPI drilldown on SalesScreen.
// Discounts drilldown KPIs: sum(discount) = Total Discounts, sum(orderCount) = Disc. Orders,
// sum(netSales)/sum(discount) = Effectiveness, top discount holder = Top Discounter.
export interface LiveStaffPerformanceItem {
  orderCount: number;
  netSales: number;
  tip: number;
  tax: number;
  discount: number;
  grandTotal: number;
  gratuity: number;
  tipAndGratuity: number | null;
  ordersWithTips: number | null;
  ordersWithGratuity: number | null;
  otherCharges: number | null;
  outStandingAmt: number;
  /** Always null on this endpoint — use by-channel for anything channel-attributed. */
  channel: string | null;
  timezoneCode: string | null;
  staff_name: string;
  refund: number | null;
  todayAvgOrder: number | null;
  guestCount: number | null;
  spendPerGuest: number | null;
}

// Response item for GET /sales/live/v2/staff-active-clocking
// Drives the Active Staff stamp KPI on the Snapshot screen for Today.
export interface LiveStaffActiveClockingItem {
  staffName: string;
  grandTotal: number;
  netSales: number;
  lastOrderDate: string | null;
  firstClockIn: string | null;
  totalHours: string | null;
  currentStatus: string;
}

// Response item for GET /sales/live/v2/staff-active-clocking-v2
// Drives the Staff Roster drilldown (paginated/searchable/sortable, any date range)
// under the Active Staff stamp KPI on the Snapshot screen.
export interface LiveStaffActiveClockingV2Item {
  date: string | null;
  employeeName: string;
  clockIn: string | null;
  clockOut: string | null;
  workedHours: string | null;
  breakHours: string | null;
  status: string | null;
  grandTotal: number;
  netSales: number;
}

// Response for GET /sales/live/v2/staff-active-clocking-v2
export interface LiveStaffActiveClockingV2Response {
  content: LiveStaffActiveClockingV2Item[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  staffWorked: number;
  activeCount: number;
  breakCount: number;
  totalWorkedHours: number;
  totalNetSales: number;
  avgSalesPerStaff: number;
}

// Response for GET /sales/live/v2/order-detail
export interface OrderDetailItem {
  itemName: string;
  modifiers: string;
  qty: number;
  unitPrice: number;
  lineTotal: number;
}

export interface RemovedItemModifier {
  name: string;
  price: number;
}

export interface RemovedItem {
  item: string;
  modifiers: RemovedItemModifier[];
  quantity: number;
  unitPrice?: number;
  total: number;
  cancelReason: string;
}

export interface OrderDetailResponse {
  orderId: string;
  orderType: string;
  orderTime: string;
  orderStatus: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  guests?: number;
  tableName?: string | null;
  deliveryAddress: string | null;
  items: OrderDetailItem[];
  subtotal: number;
  discounts: number;
  tax: number;
  tip: number;
  total: number;
  gratuity?: number;
  deliveryCharges?: number;
  convenienceFee?: number;
  cardProcessingFee?: number;
  staffName: string;
  removedItems?: RemovedItem[];
}

// Response item for GET /sales/live/v2/order-log
export interface OrderLogItem {
  orderNo: string;
  orderId: string;
  orderDate?: string;
  orderTime: string;
  orderType: string;
  customerName: string;
  customerPhone: string;
  guests?: number;
  itemPreview: string;
  amount: number;
}

export interface OrderLogResponse {
  orders: OrderLogItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

// Response item for GET /sales/live/v2/unpaid-orders (Today only)
export interface UnpaidOrderItem {
  orderNo: string;
  orderId: string;
  orderDate?: string;
  orderTime: string;
  serverName: string;
  orderType: string;
  itemPreview?: string;
  amount: number;
  customerName?: string;
  guestCount?: number;
  tableName?: string | null;
}

export interface UnpaidOrdersResponse {
  orders: UnpaidOrderItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

// ── Complimentary Orders ─────────────────────────────────────────────────────

// GET /sales/live/v2/comp-order-log
export interface CompOrderLogItem {
  orderNo: string;
  orderId: string;
  orderTime: string;
  staffName: string;
  cancelReason: string;
  items: string[];
  amount: number;
}

export interface CompOrderLogResponse {
  orders: CompOrderLogItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

// GET /sales/live/v2/comp-reasons
export interface CompReasonItem {
  cancelReason: string;
  occurrenceCount: number;
  totalAmount: number;
}

export interface CompReasonsResponse {
  reasons: CompReasonItem[];
}

// GET /sales/live/v2/comp-by-staff
export interface CompByStaffItem {
  staffName: string;
  compCount: number;
  totalCompValue: number;
}

export interface CompByStaffResponse {
  staff: CompByStaffItem[];
}

// Response item for GET /sales/live/v2/categoy-sales (Today only)
export interface LiveCategorySalesItem {
  itemName: string | null;
  categoryName: string;
  quantity: number | null;
  price: number;
}

// Paginated response for GET /sales/live/v2/categoy-sales (page/size driven).
// `page` is 1-indexed (matches the Order Log convention). Older deployments may
// still return a bare LiveCategorySalesItem[] — the saga tolerates both.
export interface LiveCategorySalesResponse {
  categories: LiveCategorySalesItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

// Response item for GET /sales/live/v2/staff-refunds (Today only)
// Drives the Refunds by Staff panel + Top Refunder stamp KPI on the SalesScreen refunds drilldown.
export interface LiveStaffRefundsItem {
  orderCount: number;
  netSales: number | null;
  tip: number | null;
  tax: number | null;
  discount: number | null;
  grandTotal: number | null;
  gratuity: number | null;
  outStandingAmt: number | null;
  channel: string | null;
  timezoneCode: string | null;
  staff_name: string;
  refund: number;
  todayAvgOrder: number | null;
}

// Response for GET /sales/live/v2/snapshot-details/comparison (Today only)
// All numeric fields are percentage-change values vs the FE-derived comparison window.
//
// `currentTime` / `locationTimezone` are the server's own clock reading at the moment it
// computed those numbers — the wall clock at the STORE, in `locationTimezone`. They drive
// the "(to 7 AM)" truncation label on the date control (ZAR-1662): the label then states
// where the DATA stops, not where the viewer's device clock happens to stand. Both are
// optional so an older backend that omits them simply falls back to the browser clock.
export interface SnapshotComparisonResponse {
  orderCount: number;
  netSales: number;
  tip: number;
  tax: number;
  discount: number;
  grandTotal: number;
  gratuity: number;
  // ZAR-1762 / TG-1 — percent change on the combined tips + gratuity total. Backend-computed,
  // so the unified card never has to back-solve a combined delta from the two separate ones.
  tipAndGratuity: number;
  outStandingAmt: number;
  refund: number;
  avgOrderValue: number;
  unpaidOrder: number;
  compleOrder: number;
  guestCount: number;
  spendPerGuest?: number;
  /** Server clock at the store, "YYYY-MM-DD HH:mm:ss" in `locationTimezone`. */
  currentTime?: string;
  /** IANA zone the reading above is expressed in, e.g. "America/Chicago". */
  locationTimezone?: string;
}

export interface ClickHouseData {
  id: string;
  locationId: string;
  [key: string]: unknown;
}

export interface ClickHouseState {
  data: ClickHouseData | null;
  loading: boolean;
  error: string | null;
  snapshotDetails: SnapshotDetailsResponse | null;
  snapshotDetailsLoading: boolean;
  snapshotDetailsError: string | null;
}

// GET /sales/live/v2/labour-employee-sales
export interface LabourEmployeeSalesItem {
  employeeName: string;
  sales: number;
  orderCount: number;
  tips: number;
}

export interface LabourEmployeeSalesSummary {
  totalSales: number;
  avgSalesPerEmployee: number;
  topSeller: string;
  splh: string;
  totalTips: number | null;
  avgTipsPerEmployee: number | null;
  tipRate: number | null;
}

export interface LabourEmployeeSalesResponse {
  content: LabourEmployeeSalesItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  summary: LabourEmployeeSalesSummary;
}

// GET /sales/live/v2/labour-voids
export interface LabourVoidsReason {
  cancelReason: string;
  removedQty: number | null;
  orderCount: number;
  cancelledAmt: number | null;
}

export interface LabourVoidsByEmployee {
  employeeName: string;
  voidCount: number;
  voidAmount: number;
}

export interface LabourVoidsResponse {
  totalVoids: number;
  totalVoidAmount: number;
  avgVoidValue: number;
  reasons: LabourVoidsReason[];
  byEmployee: LabourVoidsByEmployee[];
}

// GET /sales/live/v2/labour-tips
export interface LabourTipsItem {
  employeeName: string;
  tips: number;
  tipRate: number;
}

export interface LabourTipsSummary {
  totalSales: number | null;
  avgSalesPerEmployee: number | null;
  topSeller: string | null;
  splh: string | null;
  totalTips: number;
  avgTipsPerEmployee: number;
  tipRate: number;
}

export interface LabourTipsResponse {
  content: LabourTipsItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  summary: LabourTipsSummary;
}

// GET /sales/live/v2/labour-working-hours
export interface LabourWorkingHoursItem {
  employeeName: string;
  workingHours: string;  // "Xh Ym" pre-formatted
  breakHours: string;    // "Xh Ym" pre-formatted
}

export interface LabourWorkingHoursSummary {
  totalBreakHours: string;
  netProductiveHours: string;
  avgHoursPerEmployee: string;
}

export interface LabourWorkingHoursResponse {
  content: LabourWorkingHoursItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  summary: LabourWorkingHoursSummary;
}

// GET /sales/live/v2/labour-staff-performance
export interface LabourStaffPerformanceItem {
  employeeName: string;
  totalHours: string;    // "Xh Ym" formatted string
  breakHours: string;    // "Xh Ym" formatted string
  sales: number;
  splh: number | null;
  tips: number;
  gratuity: number;
  voids: number;
  voidAmount: number;
}

export interface LabourStaffPerformanceSummary {
  totalHours: string;
  totalBreakHours: string;
  totalSales: number;
  totalSplh: number | null;
  totalTips: number;
  totalGratuity: number;
  totalVoids: number;
  totalVoidAmount: number;
}

export interface LabourStaffPerformanceResponse {
  content: LabourStaffPerformanceItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  summary: LabourStaffPerformanceSummary;
}

// GET /sales/live/v2/labour-working-hours-log
export interface LabourWorkingHoursLogItem {
  workDate: string;         // "YYYY-MM-DD"
  employeeName: string;
  clockIn: string;          // "HH:MM AM/PM" pre-formatted
  clockOut: string | null;  // "HH:MM AM/PM" pre-formatted
  status: string;
  workedHours: string;      // "Xh Ym" pre-formatted
  breakHours: string;       // "Xh Ym" pre-formatted
}

export interface LabourWorkingHoursLogResponse {
  content: LabourWorkingHoursLogItem[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

// GET /sales/labour/employees
export interface LabourEmployee {
  staffId: string;
  staffName: string;
}

export interface LabourEmployeesResponse {
  employees: LabourEmployee[];
}

// GET /sales/live/v2/labour-kpi-comparison
//
// Every metric is nullable in practice: the endpoint returns `null` for a KPI it cannot
// compute for the window (no staff clocked in, no sales to divide by, …) rather than 0.
//
// `currentTime` / `locationTimezone` mirror the snapshot comparison endpoint — the
// server's own clock reading at the store when it computed these deltas. They feed the
// date control's "(to 7 AM)" truncation label on Labor (ZAR-1662), which is why Labor no
// longer depends on the browser clock for it.
export interface LabourKpiComparisonResponse {
  totalWorkingHours: string | null;
  staffWorked: number | null;
  employeeSales: number | null;
  splh: number | null;
  totalTips: number | null;
  voids: number | null;
  totalWorkingHoursDelta: number | null;
  staffWorkedDelta: number | null;
  employeeSalesDelta: number | null;
  splhDelta: number | null;
  totalTipsDelta: number | null;
  voidsDelta: number | null;
  /** Server clock at the store, "YYYY-MM-DD HH:mm:ss" in `locationTimezone`. */
  currentTime?: string;
  /** IANA zone the reading above is expressed in, e.g. "America/Chicago". */
  locationTimezone?: string;
}

/**
 * `channelGroup` — the sales channel an order belongs to. Reported on every
 * sales-by-channel-breakdown row and accepted as the `channelGroup` filter on
 * GET /sales/live/v2/order-log (this is what the breakdown drill-down sends —
 * NOT `channelName`, which filters by the POS order-type name instead).
 *
 * DIRECT_ONLINE / IN_STORE are first-party; every other value is a third-party
 * marketplace platform.
 */
export type OrderChannelGroup =
  | 'DIRECT_ONLINE'
  | 'IN_STORE'
  | 'UberEats'
  | 'Doordash'
  | 'GrubHub'
  | 'GloriaFood'
  | 'Swiggy'
  | 'Seamless'
  | 'Unknown';

/** On-screen name per channelGroup. Marketplace platforms read as their raw value. */
export const CHANNEL_GROUP_LABELS: Record<OrderChannelGroup, string> = {
  DIRECT_ONLINE: 'Direct online',
  IN_STORE: 'Instore',
  UberEats: 'UberEats',
  Doordash: 'Doordash',
  GrubHub: 'GrubHub',
  GloriaFood: 'GloriaFood',
  Swiggy: 'Swiggy',
  Seamless: 'Seamless',
  Unknown: 'Unknown',
};

/** Label for a channelGroup; unrecognised values fall back to the raw value. */
export const channelGroupLabel = (group: string): string =>
  CHANNEL_GROUP_LABELS[group as OrderChannelGroup] ?? group;

// GET /sales/live/v2/sales-by-channel-breakdown
// One row per (channelGroup, channel) pair. `channelGroup` is DIRECT_ONLINE / IN_STORE
// for first-party sales; any other value is a third-party marketplace platform.
export interface SalesByChannelBreakdownRow {
  channelGroup: string | null;
  channel: string | null;
  orderCount: number;
  itemTotal: number;
  discount: number;
  deliveryFee: number;
  convenienceFee: number;
  serviceFee: number;
  cardProcessingFee: number;
  tips: number;
  tax: number;
  totalCollected: number;
}

export interface SalesByChannelBreakdownResponse {
  rows: SalesByChannelBreakdownRow[];
  // Grand-total row — `channelGroup` / `channel` are null.
  totals: SalesByChannelBreakdownRow | null;
}
